import moment from "moment";
import { useEffect, useState } from "react";
import {
    Modal,
    Form,
    Button,
    Row,
    Col,
    Spinner,
    Badge,
} from "react-bootstrap";
import { CompanyService } from "Services/CompanyService";

interface IPlanOption {
    _id: string;
    name: string;
    slug: string;
    price: number;
    currency: string;
    billingCycle: string;
}

interface IProps {
    show: boolean;
    subId: string;
    companyId: string;
    onHide: () => void;
    onSuccess: () => void;
}

const SUBSCRIPTION_STATUSES = ["active", "expired", "cancelled"];

export default function EditSubscription({
    show,
    subId,
    companyId,
    onHide,
    onSuccess,
}: IProps) {
    // Subscription detail
    const [detailLoading, setDetailLoading] = useState(false);
    const [subDetail, setSubDetail] = useState<any>(null);

    // Plan dropdown
    const [planOptions, setPlanOptions] = useState<IPlanOption[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);

    // Form fields
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");

    // Status update
    const [selectedStatus, setSelectedStatus] = useState("");
    const [statusSaving, setStatusSaving] = useState(false);
    const [statusError, setStatusError] = useState("");
    const [statusSuccess, setStatusSuccess] = useState("");

    // Update sub
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    // ── Fetch subscription details & plan list when modal opens ──────────────
    useEffect(() => {
        if (!show || !subId) return;
        fetchDetail();
        fetchPlans();
        // reset feedback
        setSaveError("");
        setStatusError("");
        setStatusSuccess("");
    }, [show, subId]);

    async function fetchDetail() {
        setDetailLoading(true);
        try {
            const res = await CompanyService.getSubscriptionById(subId);
            if (res.status === 200) {
                const data = res.data?.data ?? res.data ?? {};
                setSubDetail(data);
                setSelectedPlanId(data?.plan?._id ?? data?.plan ?? "");
                setStartDate(
                    data?.startDate
                        ? moment(data.startDate).format("YYYY-MM-DD")
                        : ""
                );
                setExpiryDate(
                    data?.expiryDate
                        ? moment(data.expiryDate).format("YYYY-MM-DD")
                        : ""
                );
                setSelectedStatus(data?.status ?? "");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setDetailLoading(false);
        }
    }

    async function fetchPlans() {
        if (planOptions.length > 0) return;
        setPlansLoading(true);
        try {
            const res = await CompanyService.getPlansForDropdown();
            if (res.status === 200) {
                setPlanOptions(res.data?.data ?? []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setPlansLoading(false);
        }
    }

    // ── Update subscription details ──────────────────────────────────────────
    async function handleSave() {
        if (!selectedPlanId || !startDate || !expiryDate) {
            setSaveError("All fields are required.");
            return;
        }
        setSaving(true);
        setSaveError("");
        try {
            const payload = {
                company: companyId,
                plan: selectedPlanId,
                startDate: new Date(startDate).toISOString(),
                expiryDate: new Date(expiryDate).toISOString(),
            };
            const res = await CompanyService.updateSubscription(subId, payload);
            if (res.status === 200 || res.status === 201) {
                onSuccess();
                onHide();
            } else {
                setSaveError(
                    res.data?.message ?? "Failed to update subscription."
                );
            }
        } catch (err: any) {
            setSaveError(
                err?.response?.data?.message ??
                    "Something went wrong. Please try again."
            );
        } finally {
            setSaving(false);
        }
    }

    // ── Update status ────────────────────────────────────────────────────────
    async function handleStatusUpdate() {
        if (!selectedStatus) {
            setStatusError("Please select a status.");
            return;
        }
        setStatusSaving(true);
        setStatusError("");
        setStatusSuccess("");
        try {
            const res = await CompanyService.updateSubscriptionStatus(subId, {
                status: selectedStatus,
            });
            if (res.status === 200 || res.status === 201) {
                setStatusSuccess("Status updated successfully.");
                setSubDetail((prev: any) => ({ ...prev, status: selectedStatus }));
                onSuccess(); // refresh parent list silently
            } else {
                setStatusError(
                    res.data?.message ?? "Failed to update status."
                );
            }
        } catch (err: any) {
            setStatusError(
                err?.response?.data?.message ??
                    "Something went wrong. Please try again."
            );
        } finally {
            setStatusSaving(false);
        }
    }

    // ── Helpers ──────────────────────────────────────────────────────────────
    function statusBadgeVariant(status: string) {
        switch (status?.toLowerCase()) {
            case "active":
                return "success";
            case "expired":
                return "warning";
            case "cancelled":
                return "danger";
            default:
                return "secondary";
        }
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Edit Subscription</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {detailLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading subscription details…
                    </div>
                ) : (
                    <>
                        {/* ── Current info banner ── */}
                        {subDetail && (
                            <div className="assigned-plan-card p-3 mb-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>{subDetail?.plan?.name ?? "—"}</strong>
                                        <div className="text-muted small">
                                            {subDetail?.plan?.currency}{" "}
                                            {subDetail?.plan?.price} /{" "}
                                            {subDetail?.plan?.billingCycle}
                                        </div>
                                    </div>
                                    <Badge bg={statusBadgeVariant(subDetail?.status)}>
                                        {subDetail?.status}
                                    </Badge>
                                </div>
                            </div>
                        )}

                        {/* ── Section 1: Update details ── */}
                        <h6 className="fw-bold mb-3">Update Subscription Details</h6>
                        <Form>
                            <Form.Group className="mb-3" controlId="editPlan">
                                <Form.Label className="fw-semibold">Plan</Form.Label>
                                {plansLoading ? (
                                    <div className="text-muted small">
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Loading plans…
                                    </div>
                                ) : (
                                    <Form.Select
                                        value={selectedPlanId}
                                        onChange={(e) => {
                                            setSelectedPlanId(e.target.value);
                                            setSaveError("");
                                        }}
                                    >
                                        <option value="">— Select a plan —</option>
                                        {planOptions.map((p) => (
                                            <option key={p._id} value={p._id}>
                                                {p.name} — {p.currency} {p.price} /{" "}
                                                {p.billingCycle}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="editStartDate">
                                        <Form.Label className="fw-semibold">
                                            Start Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => {
                                                setStartDate(e.target.value);
                                                setSaveError("");
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="editExpiryDate">
                                        <Form.Label className="fw-semibold">
                                            Expiry Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={expiryDate}
                                            min={startDate}
                                            onChange={(e) => {
                                                setExpiryDate(e.target.value);
                                                setSaveError("");
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {saveError && (
                                <div className="text-danger small mb-2">{saveError}</div>
                            )}
                        </Form>

                        <hr className="my-4" />

                        {/* ── Section 2: Update status only ── */}
                        <h6 className="fw-bold mb-3">Update Status</h6>
                        <div className="d-flex align-items-center gap-3">
                            <Form.Select
                                value={selectedStatus}
                                onChange={(e) => {
                                    setSelectedStatus(e.target.value);
                                    setStatusError("");
                                    setStatusSuccess("");
                                }}
                                style={{ maxWidth: 220 }}
                            >
                                <option value="">— Select status —</option>
                                {SUBSCRIPTION_STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </option>
                                ))}
                            </Form.Select>

                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={handleStatusUpdate}
                                disabled={statusSaving}
                            >
                                {statusSaving ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-1"
                                        />
                                        Saving…
                                    </>
                                ) : (
                                    "Update Status"
                                )}
                            </Button>
                        </div>

                        {statusError && (
                            <div className="text-danger small mt-2">{statusError}</div>
                        )}
                        {statusSuccess && (
                            <div className="text-success small mt-2">{statusSuccess}</div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    className="save-plan-btn"
                    onClick={handleSave}
                    disabled={saving || detailLoading || plansLoading}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving…
                        </>
                    ) : (
                        "Save Changes"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
